/**
 * Sessão de uso (docs/telemetria.md, seção 2; análise, seção 4.3).
 *
 * - A sessão vale para o navegador inteiro (localStorage): abas abertas juntas dividem a sessão.
 *   Trinta minutos sem atividade abrem outra.
 * - Cada aba tem o seu id e a sua sequência (sessionStorage), para a linha do tempo sair na
 *   ordem certa mesmo com lotes atrasados.
 * - O tempo ativo conta só com a aba visível e com interação no último minuto. Ele não vira
 *   evento: viaja no lote e o servidor soma na sessão.
 */

export const SESSION_IDLE_MS = 30 * 60 * 1000;
/** Sem interação por mais que isso, a aba visível deixa de contar como tempo ativo. */
export const ACTIVE_WINDOW_MS = 60 * 1000;
const TICK_MS = 5 * 1000;

const SESSION_KEY = 'lm:activity:session';
const TAB_KEY = 'lm:activity:tab';

interface StoredSession {
  id: string;
  lastAt: number;
}

interface StoredTab {
  id: string;
  seq: number;
}

export function newId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid;
  // Navegador sem randomUUID: id de correlação no formato v4, não segredo.
  const hex = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16));
  hex[12] = '4';
  hex[16] = ((parseInt(hex[16] ?? '0', 16) & 0x3) | 0x8).toString(16);
  const s = hex.join('');
  return `${s.slice(0, 8)}-${s.slice(8, 12)}-${s.slice(12, 16)}-${s.slice(16, 20)}-${s.slice(20)}`;
}

function read<T>(storage: Storage | undefined, key: string): T | null {
  try {
    const raw = storage?.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(storage: Storage | undefined, key: string, value: unknown): void {
  try {
    storage?.setItem(key, JSON.stringify(value));
  } catch {
    /* storage cheio ou bloqueado: a sessão segue só em memória nesta aba */
  }
}

function local(): Storage | undefined {
  return typeof localStorage === 'undefined' ? undefined : localStorage;
}

function perTab(): Storage | undefined {
  return typeof sessionStorage === 'undefined' ? undefined : sessionStorage;
}

let memorySession: StoredSession | null = null;
let memoryTab: StoredTab | null = null;

/** Sessão atual, trocada depois de 30 minutos parada; marca a atividade de agora. */
export function touchSession(now = Date.now()): string {
  const stored = read<StoredSession>(local(), SESSION_KEY) ?? memorySession;
  const session =
    stored && typeof stored.id === 'string' && now - stored.lastAt <= SESSION_IDLE_MS
      ? { id: stored.id, lastAt: now }
      : { id: newId(), lastAt: now };
  memorySession = session;
  write(local(), SESSION_KEY, session);
  return session.id;
}

/** Sessão atual sem marcar atividade (header dos pedidos à API). `null` se já expirou. */
export function currentSessionId(now = Date.now()): string | null {
  const stored = read<StoredSession>(local(), SESSION_KEY) ?? memorySession;
  if (!stored || now - stored.lastAt > SESSION_IDLE_MS) return null;
  return stored.id;
}

/** Id da aba e o próximo número da sequência dela. */
export function nextTabSeq(): { tabId: string; seq: number } {
  const stored = read<StoredTab>(perTab(), TAB_KEY) ?? memoryTab;
  const tab = stored && typeof stored.id === 'string' ? stored : { id: newId(), seq: 0 };
  const next = { id: tab.id, seq: tab.seq + 1 };
  memoryTab = next;
  write(perTab(), TAB_KEY, next);
  return { tabId: next.id, seq: next.seq };
}

export interface ActiveTimer {
  /** Tempo ativo acumulado por sessão desde a última retirada; zera o acumulado. */
  drain(): Record<string, number>;
  stop(): void;
}

/**
 * Conta o tempo ativo desta aba. `now`, `visible` e `schedule` existem para os testes.
 */
export function startActiveTimer(
  options: {
    now?: () => number;
    visible?: () => boolean;
    schedule?: (fn: () => void, ms: number) => () => void;
  } = {},
): ActiveTimer {
  const now = options.now ?? (() => Date.now());
  const visible =
    options.visible ??
    (() => typeof document === 'undefined' || document.visibilityState === 'visible');
  const schedule =
    options.schedule ??
    ((fn: () => void, ms: number) => {
      const id = setInterval(fn, ms);
      return () => clearInterval(id);
    });

  const accumulated: Record<string, number> = {};
  let lastInteraction = now();
  let lastTick = now();

  const markInteraction = () => {
    lastInteraction = now();
  };
  const events = ['pointerdown', 'keydown', 'scroll', 'wheel', 'mousemove'] as const;
  if (typeof window !== 'undefined') {
    for (const name of events) window.addEventListener(name, markInteraction, { passive: true });
  }

  const tick = () => {
    const at = now();
    const elapsed = Math.min(at - lastTick, TICK_MS * 3);
    lastTick = at;
    if (elapsed <= 0 || !visible() || at - lastInteraction > ACTIVE_WINDOW_MS) return;
    const sessionId = touchSession(at);
    accumulated[sessionId] = (accumulated[sessionId] ?? 0) + elapsed;
  };
  const cancel = schedule(tick, TICK_MS);

  return {
    drain() {
      tick();
      const out: Record<string, number> = {};
      for (const [id, ms] of Object.entries(accumulated)) {
        if (ms > 0) out[id] = Math.round(ms);
        delete accumulated[id];
      }
      return out;
    },
    stop() {
      cancel();
      if (typeof window !== 'undefined') {
        for (const name of events) window.removeEventListener(name, markInteraction);
      }
    },
  };
}

export function __resetActivitySession(): void {
  memorySession = null;
  memoryTab = null;
  try {
    local()?.removeItem(SESSION_KEY);
    perTab()?.removeItem(TAB_KEY);
  } catch {
    /* noop */
  }
}
